import Cookies from "js-cookie";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import sha256 from "sha256";
import environment from "src/environment";
import GetMe from "src/hoc/repository/withMe/graphql/GetMe";
import { useIsMountedFunctions } from "src/hooks/useIsMountedFunctions";
import i18n from "src/i18n";
import client from "src/lib/graphql";
import { mapTarget2SellProductToProduct } from "src/services/mapperService";
import { ITarget2SellPageParams } from "src/types/global";
import { IProduct } from "src/types/product";

import { setT2SLoaded } from "../redux/action";
import { getT2SLoaded } from "../redux/selector";

import addT2sScript from "./addT2sScript";
import { sendT2Sevent } from "./sendT2sEvent";

type IProducts = IProduct[] | null;

interface IProps {
  spaceId: string;
}

interface IOutputs {
  t2sProducts: IProducts;
  onT2SProductClicked: (product: IProduct, productIndex: number) => void;
}

const useT2sProducts = ({ spaceId }: IProps): IOutputs => {
  const [products, setProducts] = useState<IProducts>(null);
  const { isMounted } = useIsMountedFunctions();

  // T2S is loaded (redux)
  const isT2sLoaded = useSelector(getT2SLoaded);
  const dispatch = useDispatch();

  /**
   * Set T2S Products
   */
  const intervalId = useRef<number | null>(null);

  const setT2SProducts = useCallback((): void => {
    let count: number = 0;
    let t2sContainer: HTMLElement | null;

    if (!intervalId.current && !products) {
      intervalId.current = setInterval(() => {
        if (count >= 10 && intervalId.current) {
          clearInterval(intervalId.current);
        }

        t2sContainer = document.getElementById(
          `${environment.T2S.CID}-${spaceId}`
        );

        // T2S container found
        if (t2sContainer && !!t2sContainer.textContent) {
          try {
            if (intervalId.current) {
              clearInterval(intervalId.current);
            }

            const response: {
              values: [];
            } = JSON.parse(t2sContainer.textContent);

            if (isMounted()) {
              setProducts(mapTarget2SellProductToProduct(response.values));
            }
          } catch (e) {
            // tslint:disable-next-line: no-console
            console.error(e);

            if (isMounted()) {
              setProducts([]);
            }
          }
        }
        count++;
      }, 100);
    }
  }, [isMounted, products, spaceId]);

  /**
   * Initialization
   */
  const init = useCallback((): void => {
    dispatch(
      setT2SLoaded({
        t2sLoaded: true,
      })
    );

    if (!window.T2SRecoStarted && window.T2S?.reco) {
      window.T2SRecoStarted = true;
      window.T2S.reco();
    }

    if (!products) {
      setT2SProducts();
    }
  }, [dispatch, products, setT2SProducts]);

  /**
   * Init T2S script
   */
  useEffect(() => {
    if (spaceId) {
      const t2sPageParams = window._t2sparams || ({} as ITarget2SellPageParams);
      t2sPageParams.cID = environment.T2S.CID;
      t2sPageParams.pID = spaceId.split("-")[0];
      t2sPageParams.eN = "view";
      t2sPageParams.lang = i18n.language.toUpperCase();
      t2sPageParams.hasRankOption = true;

      // T2S params based on Me
      (async (): Promise<void> => {
        const getMeResponse = await client.query({
          fetchPolicy: "cache-only",
          query: GetMe,
        });

        if (getMeResponse?.data?.me) {
          t2sPageParams.uEM = sha256(
            getMeResponse.data.me.email.trim().toLowerCase()
          );
          t2sPageParams.uID = Cookies.get("t2s-userId");
        }
      })();

      window._t2sparams = t2sPageParams;

      const scriptT2S = document.getElementById("t2sScript");

      if (!scriptT2S) {
        addT2sScript(init);
      } else {
        init();
      }
    }

    return (): void => {
      window.T2SRecoStarted = false;
    };
  }, [init, spaceId]);

  /**
   * Update products
   */
  useEffect(() => {
    const scriptT2S = document.getElementById("t2sScript");

    if (scriptT2S && isT2sLoaded && !products) {
      setT2SProducts();
    }
  }, [products, setT2SProducts, isT2sLoaded]);

  /**
   * Send event params on clicked product to T2S
   * @param product
   * @param productIndex
   */
  const onT2SProductClicked = (product: IProduct, productIndex: number): void =>
    sendT2Sevent(spaceId, product, productIndex);

  /**
   * useT2SProducts hook output
   */
  return {
    t2sProducts: products,
    onT2SProductClicked,
  };
};

export default useT2sProducts;
